import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../../util/reactIntl';

import {
  ExternalLink,
  IconArrowHead,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  NamedLink,
  OutsideClickHandler,
} from '../../../../../components';

import css from './PriorityLinks.module.css';

/**
 * Create component that shows only a single "Post a new listing" link.
 *
 * @param {*} props contains customLinksMenuClass
 * @returns div with only one link inside.
 */
export const CreateListingMenuLink = props => {
  return (
    <div className={props.customLinksMenuClass}>
      <NamedLink name="NewListingPage" className={classNames(css.priorityLink, css.highlight)}>
        <span className={css.priorityLinkLabel}>
          <FormattedMessage id="TopbarDesktop.createListing" />
        </span>
      </NamedLink>
    </div>
  );
};

/**
 * Link component that can be used on TopbarDesktop.
 *
 * @param {*} props containing linkConfig including resolved 'route' params for NamedLink.
 * @returns NamedLink or ExternalLink component based on config.
 */
export const PriorityLink = ({ linkConfig, currentPage }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { text, type, href, route, highlight, sections } = linkConfig;
  const classes = classNames(css.priorityLink, {
    [css.highlight]: highlight,
    [css.currentPage]: route?.name === currentPage,
  });
  const id = `priority-link-${text.toLowerCase().replace(/ /g, '-')}`;

  const dropdownItems = sections?.flatMap((section, sectionIndex) => {
    const sectionKey = `${text}_${section.label || sectionIndex}`;
    const labelItem = section.label
      ? [
          <MenuItem key={`${sectionKey}_label`}>
            <div className={css.dropdownSectionLabel}>{section.label}</div>
          </MenuItem>,
        ]
      : [];
    const linkItems = section.items.map((item, itemIndex) => {
      const itemRoute = item.route || {};
      return (
        <MenuItem key={`${sectionKey}_${item.text}_${itemIndex}`}>
          {item.disabled ? (
            <span className={css.dropdownDisabled} aria-disabled="true">
              {item.text}
            </span>
          ) : (
            <div onClick={() => setIsDropdownOpen(false)}>
              {item.type === 'internal' && item.route ? (
                <NamedLink
                  name={itemRoute.name}
                  params={itemRoute.params}
                  to={itemRoute.to}
                  className={css.dropdownLink}
                >
                  <span className={css.menuItemBorder} />
                  {item.text}
                </NamedLink>
              ) : (
                <ExternalLink href={item.href} className={css.dropdownLink}>
                  <span className={css.menuItemBorder} />
                  {item.text}
                </ExternalLink>
              )}
            </div>
          )}
        </MenuItem>
      );
    });

    return [...labelItem, ...linkItems];
  });

  if (sections?.length > 0 && type === 'internal' && route) {
    const { name, params, to } = route || {};
    return (
      <OutsideClickHandler
        rootClassName={css.priorityDropdown}
        onOutsideClick={() => setIsDropdownOpen(false)}
      >
        <NamedLink name={name} params={params} to={to} className={classes} id={id}>
          <span className={css.priorityLinkLabel}>{text}</span>
        </NamedLink>
        <Menu
          id={`${id}-menu`}
          contentPosition="left"
          isOpen={isDropdownOpen}
          onToggleActive={setIsDropdownOpen}
          skipFocusOnNavigation={false}
        >
          <MenuLabel
            id={`${id}-toggle`}
            className={css.priorityDropdownToggle}
            isOpenClassName={css.priorityDropdownOpen}
            ariaLabel={`${text} menu`}
          >
            <IconArrowHead direction="down" size="tiny" rootClassName={css.dropdownChevron} />
          </MenuLabel>
          <MenuContent className={css.priorityDropdownContent}>{dropdownItems}</MenuContent>
        </Menu>
      </OutsideClickHandler>
    );
  }

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    return (
      <NamedLink name={name} params={params} to={to} className={classes} id={id}>
        <span className={css.priorityLinkLabel}>{text}</span>
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={classes}>
      <span className={css.priorityLinkLabel}>{text}</span>
    </ExternalLink>
  );
};

/**
 * Create priority links, which are visible on the desktop layout on the Topbar.
 * If space is limited, this doesn't include anything to the Topbar.
 *
 * @param {*} props contains links array and setLinks function
 * @returns list of priority links.
 */
const PriorityLinks = props => {
  const containerRef = useRef(null);

  // With this useEffect, we measure the widths of each rendered priority link
  // This is done once before the real rendering and it's done outside the viewport.
  useEffect(() => {
    const isMeasured = props.links?.[0]?.width;
    if (containerRef.current && !isMeasured) {
      const linksFromRenderedWrapper = [...containerRef.current.childNodes];
      let cumulatedWidth = 0;
      // Generate an array of link configs with width & cumulatedWidth included
      const linksWithWidths = props.links.reduce((links, l, i) => {
        const width = linksFromRenderedWrapper[i].offsetWidth;
        cumulatedWidth = cumulatedWidth + width;
        return [...links, { ...l, width, cumulatedWidth }];
      }, []);
      props.setLinks(linksWithWidths);
    }
  }, [containerRef]);

  const { links, priorityLinks } = props;
  const isMeasured = links?.[0]?.width && (priorityLinks.length === 0 || priorityLinks?.[0]?.width);
  const styleWrapper = !!isMeasured
    ? {}
    : {
        style: {
          position: 'absolute',
          top: '-2000px',
          left: '-2000px',
          width: '100%',
          height: 'var(--topbarHeightDesktop)',
          display: 'flex',
          flexDirection: 'row',
        },
      };
  const linkConfigs = isMeasured ? priorityLinks : links;

  // Always render inline in the Topbar tree so SSR and the initial client render match.
  // (Portaling to document.body caused hydration mismatches.)
  return (
    <ul className={css.priorityLinkWrapper} {...styleWrapper} ref={containerRef}>
      {linkConfigs.map((linkConfig, index) => {
        return (
          <li key={`${linkConfig.text}_${index}`} className={css.priorityLinkItem}>
            <PriorityLink linkConfig={linkConfig} currentPage={props.currentPage} />
          </li>
        );
      })}
    </ul>
  );
};

export default PriorityLinks;
